import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@fragments';
import { IconAlertCircle, IconBackground, IconMovie, IconUpload } from '@tabler/icons-react';
import { useArtEditorContext } from '../../../context';
import { useImagePages } from '../../ImageGrid';
import { VIDEO_LIMITS } from '../../../../model/schema';
import { roomFor } from '../../../../model/timeline';
import { secondsLabel } from '../../Timeline';

export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm';
// A video of the creator's (`{ id, url, width, height, duration }`) as the source a clip plays.
export const videoSource = video => ({ kind: 'asset', asset_id: video.id, url: video.url, width: video.width, height: video.height, duration: video.duration });

// The creator's videos (2.13.0): send one (or drop it on the art) and trim it, or pick one
// already sent. Either way the trim dialog opens; a tile's micro button opens it as the
// page's background (joined after the background's clips). The page's 60 s are shown.
export default function VideosPanel({ onClose }) {
  const { t, i18n } = useTranslation('artEditor');
  const { editor, videos, readOnly, openVideo, videoUploads = 0 } = useArtEditorContext();
  const input = useRef(null);
  const list = useImagePages(videos?.list ? cursor => videos.list({ cursor }) : null, `videos:${videoUploads}`);
  const page = editor.page;
  const background = page ? roomFor(page, editor.document, { background: true }) : VIDEO_LIMITS.pageSeconds;
  const pick = (video, asBackground = false) => { openVideo({ video, background: asBackground }); onClose?.(); };
  const send = file => { openVideo({ file, background: false }); onClose?.(); };
  return <>
    <section className="art-panel-section">
      <input ref={input} type="file" accept={VIDEO_ACCEPT} hidden onChange={event => { const [file] = event.target.files; event.target.value = ''; if (file) send(file); }} />
      <Button fullWidth color="inverse" variant="rounded" startIcon={<IconUpload />} disabled={readOnly || !videos?.upload} preventLoading onClick={() => input.current?.click()}>
        {t('uploadVideo')}
      </Button>
      <Text.caption contrast={5}>{t('uploadVideoHint', { seconds: VIDEO_LIMITS.pageSeconds, size: Math.round((videos?.maxBytes || 300 * 1024 * 1024) / 1024 / 1024) })}</Text.caption>
      {background < VIDEO_LIMITS.pageSeconds && <Text.caption contrast={6}>{t('videoBackgroundRoom', { left: secondsLabel(background, i18n.language) })}</Text.caption>}
    </section>
    {videos?.list && <section className="art-panel-section">
      <Text.subheading contrast={6} left={<IconMovie />}>{t('yourVideos')}</Text.subheading>
      {list.status === 'error' && <div className="art-inline-error" role="alert">
        <Text.caption contrast={8} left={<IconAlertCircle />}>{t('videosError')}</Text.caption>
        <Button size="small" variant="text" color="overlay" preventLoading onClick={() => list.reload()}>{t('retry')}</Button>
      </div>}
      {list.status === 'loading' && !list.items.length && <div className="art-image-grid"><div className="art-image-grid__tiles" aria-hidden="true">
        {Array.from({ length: 3 }, (_, index) => <span key={index} className="art-image-grid__tile art-skeleton" />)}
      </div></div>}
      {list.status !== 'loading' && list.status !== 'error' && !list.items.length && <div className="art-empty"><IconMovie aria-hidden="true" /><Text.caption contrast={6}>{t('videosEmpty')}</Text.caption></div>}
      {list.items.length > 0 && <div className="art-image-grid"><div className="art-image-grid__tiles" role="group" aria-label={t('yourVideos')}>
        {list.items.map(video => <div key={video.id} className="art-image-grid__item art-video-tile">
          <button type="button" className="art-image-grid__tile" disabled={readOnly} aria-label={`${t('trimTitleAdd')}: ${secondsLabel(video.duration, i18n.language)}`} onClick={() => pick(video)}>
            <video src={`${video.url}#t=0.1`} muted playsInline preload="metadata" aria-hidden="true" />
            <span className="art-video-tile__length"><Text.caption contrast={9}>{secondsLabel(video.duration, i18n.language)}</Text.caption></span>
          </button>
          <Button className="art-image-grid__action" size="micro" color="inverse" variant="rounded" preventLoading icon={<IconBackground />}
            disabled={readOnly || background < VIDEO_LIMITS.minClipSeconds} aria-label={t('useVideoAsBackground')} tooltip={t('useVideoAsBackground')} onClick={() => pick(video, true)} />
        </div>)}
      </div>
      {list.next && <Button className="art-image-grid__more" size="small" variant="text" color="overlay" preventLoading loading={list.status === 'more'} onClick={list.more}>{t('loadMore')}</Button>}
      </div>}
    </section>}
  </>;
}
